import type { EidCardAddress } from './eidReader';

/**
 * Splits the single free-text domicile line stored on a Romanian eID chip into
 * the address components the client form expects.
 *
 * The card stores the address as one string built from abbreviated markers, e.g.
 * `Jud.XX Sat.Exemplu (Com.Test) nr.281` or
 * `Jud.XX Mun.Oraşul Exemplu Str.Gheorghe Doja nr.12 bl.A sc.2 et.3 ap.45`.
 * Each marker introduces a value that runs until the next marker.
 *
 * This lived in the backend while a server-side helper did the reading. The chip returns one
 * address string and the workstation service passes it through unparsed, so the split now
 * happens where the read happens — in the browser. The logic is unchanged; moving it kept the
 * client form's county/city/street fields filling exactly as they did before.
 */

/** Marker abbreviation -> address field it introduces. */
const MARKER_FIELDS: Record<string, keyof EidCardAddress> = {
    jud: 'county',
    mun: 'city',
    ors: 'city',
    oras: 'city',
    sat: 'city',
    com: 'city',
    str: 'street',
    bd: 'street',
    'b-dul': 'street',
    cal: 'street',
    ale: 'street',
    pta: 'street',
    nr: 'streetNumber',
    bl: 'building',
    sc: 'entrance',
    et: 'floor',
    ap: 'apartment',
};

const MARKER_PATTERN = /\b(jud|mun|ors|oras|sat|com|str|bd|b-dul|cal|ale|pta|nr|bl|sc|et|ap)\.\s*/gi;

interface MarkerMatch {
    field: keyof EidCardAddress;
    valueStart: number;
    markerStart: number;
}

/**
 * A marker inside parentheses qualifies the preceding value rather than starting
 * a new component — `Sat.Exemplu (Com.Test)` is one locality, not two.
 */
function isInsideParentheses(text: string, index: number): boolean {
    const open = text.lastIndexOf('(', index);
    if (open === -1) {
        return false;
    }
    return text.lastIndexOf(')', index) < open;
}

/** Parses the chip's domicile line; returns {} when nothing can be recognised. */
export function parseRomanianAddress(rawAddress?: string): EidCardAddress {
    const text = rawAddress?.trim() ?? '';
    if (text.length === 0) {
        return {};
    }

    const matches: MarkerMatch[] = [];
    MARKER_PATTERN.lastIndex = 0;
    let match = MARKER_PATTERN.exec(text);
    while (match !== null) {
        const abbreviation = match[1].toLowerCase();
        const field = MARKER_FIELDS[abbreviation];
        if (field !== undefined && !isInsideParentheses(text, match.index)) {
            matches.push({
                field,
                markerStart: match.index,
                valueStart: match.index + match[0].length,
            });
        }
        match = MARKER_PATTERN.exec(text);
    }

    if (matches.length === 0) {
        return {};
    }

    const address: EidCardAddress = {};
    matches.forEach((current, index) => {
        const end = index + 1 < matches.length ? matches[index + 1].markerStart : text.length;
        const value = text.slice(current.valueStart, end).trim();
        // Keep the first occurrence: later markers of the same kind are qualifiers.
        if (value.length > 0 && address[current.field] === undefined) {
            address[current.field] = value;
        }
    });

    return address;
}
